<template>
  <div class="row poppins h-100">
    <div class="col-12 h-100">
      <div class="table-wrapper h-100 d-flex flex-column">
        <div class="filter-block">
          <SearchInput :search-change="searchChange" />
          <div class="action-buttons-wrapper">
            <b-button
              variant="primary"
              size="lg"
              class="ml-3 pl-3 pr-4 d-flex align-items-center"
              @click="goToCreate"
            >
              <BIconPlus class="mr-2" />
              {{ `Add QR Code` | i18n }}
            </b-button>
            <QRCodeModal id="QRCodeModal" :expiresIn="expiresIn" :userId="userId" :qrCodeId="qrCodeId" :scope="scope" />
            <b-button
              variant="outline-danger"
              size="lg"
              class="ml-3 pl-4 pr-4 d-flex align-items-center"
              :disabled="!selectedList.length"
              @click="deleteInstance"
            >
              <img :src="deleteIconUrl" class="mr-2" />
              {{ `Remove QR Code` | i18n }}
            </b-button>
          </div>
        </div>
        <Table
          :update="getPage"
          :columns="columns"
          :rows="rows"
          :filter="filter"
          :update-filter="updateFilter"
          :loading-instance="loadingInstance"
          :set-loading-instance="setLoadingInstance"
          :selected-list="selectedList"
          :set-selected-list="setSelectedList"
          class="overflow-auto pr-1"
          tableName="qrManagementTable"
        />
      </div>
    </div>
  </div>
</template>
<script>
import moment from 'moment';
import { BIconPlus } from 'bootstrap-vue';
import api from '../../../api';
import QRCodeModal from '../../../modals/QRCode';

import { DeleteRowMixin, NotificationMixin } from '../../../../mixins';
import PageHeader from '../../common/page-header';
import Table from '../../common/table';
import SearchInput from '../../common/search-input';
import DeleteIcon from '../../../../assets/icons/delete.svg';

import { API_ACCESS_LEVEL, API_ACCESS_PERMISION } from '../../../../../../../constants';

let inputInterval = null;

export default {
  title: 'QR Managment',
  components: { PageHeader, BIconPlus, Table, SearchInput, QRCodeModal },
  mixins: [DeleteRowMixin, NotificationMixin],
  data() {
    return {
      deleteIconUrl: DeleteIcon,
      scope: {
        [API_ACCESS_LEVEL.REPORT]: [API_ACCESS_PERMISION.WRITE],
        [API_ACCESS_LEVEL.FEED]: '*',
        [API_ACCESS_LEVEL.LIST]: [API_ACCESS_PERMISION.READ],
      },
      columns: [
        {
          field: 'check',
          key: '1',
          type: 'checkbox',
          title: '',
          width: 50,
          align: 'center',
        },
        {
          field: 'teamName',
          title: this.$translate('team'),
          key: '2',
          align: 'left',
        },
        {
          field: 'title',
          title: this.$translate('title'),
          key: '3',
          align: 'left',
        },
        {
          field: 'templateTitle',
          title: this.$translate('template'),
          key: '4',
          align: 'left',
        },
        {
          field: 'userName',
          title: this.$translate('Assigned User'),
          key: '5',
          align: 'left',
        },
        {
          field: 'expiresIn',
          title: this.$translate('expiry_time'),
          key: '6',
          sortBy: '',
          align: 'left',
          renderBodyCell: ({ row }) => {
            const getTimeMessage = (t) => {
              switch (String(t)) {
                case 'never':
                  return this.$translate('Never');
                case '86400':
                  return `1 ${this.$translate('day')}`;
                case '2592000':
                  return `30 ${this.$translate('days')}`;
              }
            };
            return <span>{getTimeMessage(row.expiresIn)}</span>;
          },
        },
        {
          field: 'actions',
          title: '',
          key: '10',
          width: 240,
          renderBodyCell: ({ row }) => {
            return (
              <div class="action-block">
                <div class="copy-button" on-click={() => this.goToDetails(row.id)}>
                  {this.$translate('Details')}
                </div>
                <div class="copy-button" on-click={() => this.showQRCode(row)}>
                  {this.$translate('QR Code')}
                </div>
              </div>
            );
          },
        },
      ],
      selectedList: [],
      selectedInstance: {},
      loadingInstance: null,
      filter: {
        page: 1,
        limit: 20,
        totalCount: 0,
        sortBy: '',
        sortOrder: '',
        searchQuery: '',
      },
      rows: [],
      qrData: {},
      expiresIn: '',
      userId: '',
      qrCodeId: '',
    };
  },
  methods: {
    goToCreate() {
      this.$router.push({ name: 'create-qr' });
    },
    goToDetails(id) {
      this.$router.push({ name: 'qr-details', params: { id } });
    },
    showQRCode(d) {
      this.expiresIn = d.expiresIn;
      this.userId = d.user.id;
      this.qrCodeId = d.id;
      setTimeout(() => {
        this.$root.$emit('bv::show::modal', 'QRCodeModal');
      }, 10);
    },
    updateFilter(f) {
      this.filter = {
        ...this.filter,
        ...f,
      };
    },
    setSelectedList(list) {
      this.selectedList = list;
    },
    setLoadingInstance(loadingInstance) {
      this.loadingInstance = loadingInstance;
    },
    searchChange(e) {
      const v = e.target.value;
      this.filter.searchQuery = v;
      if (inputInterval) {
        clearTimeout(inputInterval);
      }
      inputInterval = setTimeout(() => {
        this.getPage();
      }, 500);
    },
    async deleteInstance() {
      try {
        await this.deleteSelectedRows({}, async () => {
          const ids = this.selectedList.map((r) => r.id);
          await api.qrManagement.delete({ ids });
          this.rows = this.rows.filter((i) => !ids.includes(i.id));
          this.selectedList = [];
          this.getPage();
        });
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
    startLoading() {
      this.loadingInstance.show();
    },
    stopLoading() {
      this.loadingInstance.close();
    },
    async getPage() {
      try {
        this.startLoading();
        const { page, limit, sortBy, sortOrder, searchQuery } = this.filter;
        const ctx = {
          page,
          limit,
          sortBy,
          sortOrder,
          searchQuery,
        };
        const response = await api.qrManagement.get(ctx);
        response.data = response.data.map((item) => ({
          ...item,
          teamName: item.team.name,
          createdAt: moment(item.createdAt).format('DD.MM.YY HH:mm'),
          templateTitle: item.template.name,
          userName: item.user.name,
        }));
        this.filter.totalCount = response.totalCount;
        this.stopLoading();
        this.rows = response.data;
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ve-table-body {
  ::v-deep th {
    color: rgb(112, 112, 112) !important;
    background-color: rgb(239, 239, 239) !important;
    border: none !important;
    min-height: 50px;
  }
  ::v-deep td {
    color: rgb(112, 112, 112) !important;
    cursor: pointer;
  }
}

::v-deep .ve-table-cell-selection {
  border: none !important;
}

::v-deep .ve-table-sort {
  top: -4.5px;
}

::v-deep .details-action {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: rgb(34, 129, 254);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 17px;
  margin: auto;
  cursor: pointer;
  margin-left: 5px;
}

.statuses-block {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.date-range-block {
  min-width: 350px;
  text-align: center;
  margin-left: 30px;
  display: flex;
  align-items: center;
}

::v-deep .ve-select-selected-span {
  font-weight: 400;
}

.pagination-block {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
}

::v-deep .copy-button {
  background: rgb(237, 248, 254);
  border: 2px solid rgb(175, 216, 255);
  border-radius: 4px;
  padding: 2px 13px;
  margin: 0px 5px;
  color: rgb(49, 141, 255);
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  margin-right: 20px;
  cursor: pointer;
}

::v-deep .action-block {
  display: flex;
  justify-content: center;
}

::v-deep .action-buttons {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}
.action-buttons-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.action-buttons-wrapper button {
  white-space: nowrap;
}

::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
</style>
